import { OfficeTable } from "@/app/Offices/Components/OfficeTable";
import { OfficeMap } from "@/app/Offices/Components/OfficeMap";
import { SettingCard } from "@/app/Settings/Settings/Components/SettingFormCard";
import { useSettingsSupplier } from "@/app/Suppliers/Utils/useSettingsSupplier";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { OfficeCreateDialog } from "@/app/Offices/Components/OfficeCreateDialog";
import { Button } from "@/components/catalyst/button";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { updateSupplierMutation } from "@/app/Suppliers/GraphQL/supplierMutations";
import { SupplierConfirmationTimeframeForm } from "@/app/Suppliers/Forms/SupplierConfirmationTimeframeForm";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { SupplierTripMatrix } from "@/app/SupplierTrips/Components/SupplierTripMatrix";
import {
  supplierOfficeListQuery,
  supplierOfficeListVariables,
} from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { SupplierTripMatrixDialog } from "@/app/SupplierTrips/Components/SupplierTripMatrixDialog";
import { TripDefaultsPromptNotice } from "@/app/SupplierTrips/Components/TripDefaultsPromptNotice";
import {
  ArrowsRightLeftIcon,
  MapIcon,
  PlusIcon,
  TableCellsIcon,
} from "@heroicons/react/20/solid";
import { ComponentType, SVGProps, Suspense, useState } from "react";
import { useTranslations } from "use-intl";
import { cn } from "@/lib/utils";
import { SettingOpenHoursScreen } from "@/app/Settings/Settings/Screens/SettingOpenHoursScreen";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";

export function SettingsOfficeScreen() {
  const { supplierId, supplier } = useSettingsSupplier();
  const { open } = useDialog(OfficeCreateDialog);
  const t = useTranslations("settings.offices");

  if (!supplierId) {
    return null;
  }

  const addButton = (
    <Button
      LeadingIcon={PlusIcon}
      onClick={() => {
        open({
          initialValues: {
            supplier: {
              connect: supplierId,
            },
          },
        });
      }}
    >
      {t("addButton")}
    </Button>
  );

  return (
    <div className="space-y-12">
      <SettingCard
        title={t("title")}
        description={t("description")}
        buttons={addButton}
      >
        <Suspense fallback={<CardSpinner />}>
          <OfficesView supplierId={supplierId} />
        </Suspense>
      </SettingCard>

      <SettingOpenHoursScreen />

      {supplier ? <ConfirmationTimeframeCard supplier={supplier} /> : null}
    </div>
  );
}

function ConfirmationTimeframeCard({ supplier }: { supplier: SupplierRecord }) {
  const t = useTranslations("settings.offices.confirmationTimeframe");
  const { mutateAsync } = useGqlMutation(updateSupplierMutation);

  return (
    <EditableRowsProvider>
      <SettingRowsCard title={t("title")} description={t("description")}>
        <EditableSettingRow
          label={t("label")}
          value={t("hoursValue", { hours: supplier.hours_to_confirm })}
          editClassName="max-w-2xl"
          initialValues={{
            hours_to_confirm: supplier.hours_to_confirm,
            timezone: supplier.timezone,
            logistics_hours: supplier.logistics_hours,
          }}
          onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
        >
          <SupplierConfirmationTimeframeForm />
        </EditableSettingRow>
      </SettingRowsCard>
    </EditableRowsProvider>
  );
}

function OfficesView({ supplierId }: { supplierId: string }) {
  const t = useTranslations("settings.offices");
  const { open: openFullscreen } = useDialog(SupplierTripMatrixDialog);
  const [view, setView] = useState<"map" | "table" | "trips">("map");

  const { data: officeData } = useSuspenseGqlQuery(
    supplierOfficeListQuery,
    supplierOfficeListVariables(supplierId),
  );

  const hasTrips = officeData.supplierOffices.data.length > 1;
  const activeView = view === "trips" && !hasTrips ? "map" : view;

  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg bg-gray-100 p-1">
        <ViewTab
          active={activeView === "map"}
          Icon={MapIcon}
          label={t("view.map")}
          onClick={() => setView("map")}
        />
        <ViewTab
          active={activeView === "table"}
          Icon={TableCellsIcon}
          label={t("view.table")}
          onClick={() => setView("table")}
        />
        {hasTrips ? (
          <ViewTab
            active={activeView === "trips"}
            Icon={ArrowsRightLeftIcon}
            label={t("view.trips")}
            onClick={() => setView("trips")}
          />
        ) : null}
      </div>

      {activeView === "map" ? (
        <OfficeMap offices={officeData.supplierOffices.data} />
      ) : activeView === "table" ? (
        <OfficeTable
          id="settings-offices"
          queryVariables={{
            supplier_id: supplierId,
            archived: false,
          }}
        />
      ) : (
        <div className="flex h-full flex-col items-end gap-2">
          <TripDefaultsPromptNotice />
          <Suspense fallback={<CardSpinner />}>
            <SupplierTripMatrix
              supplierId={supplierId}
              showExpandButton
              onExpand={() => {
                openFullscreen({
                  supplierId: supplierId,
                });
              }}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}

function CardSpinner() {
  return (
    <div className="flex h-64 w-full items-center justify-center">
      <Spinner className="size-5" />
    </div>
  );
}

function ViewTab({
  active,
  Icon,
  label,
  onClick,
}: {
  active: boolean;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "bg-white text-gray-900 shadow-sm"
          : "text-gray-500 hover:text-gray-700",
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
